import { useState } from "react";
import { Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, fmtNum } from "@/lib/api";
import { Panel, Th, Td, btn, field } from "@/components/ui-kit";

export const XD = ["XD1", "XD2", "XD3"];
export const mailersFor = (reel, fg) => {
  const g = (reel.mondi_grade || "").replace(/\s+/g, "");
  return g ? fg.filter((m) => (m.description || "").startsWith(`${g} `)) : [];
};

const BalanceDialog = ({ reel, onClose, onDone }) => {
  const [site, setSite] = useState("XD1");
  const [kg, setKg] = useState("");
  const current = reel?.sites?.[site]?.on_hand || 0;
  const save = async () => {
    try {
      await api.post("/inventory/adjust", { part_no: reel.part_no, site_code: site, qty: Number(kg) - current, reason: "Opening balance" });
      toast.success(`${reel.part_no} at ${site} set to ${fmtNum(Number(kg))} kg`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!reel} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md border-slate-800 bg-[#1F232C] text-slate-100" data-testid="opening-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Opening balance · {reel?.part_no}</DialogTitle>
          <DialogDescription className="text-slate-400">{reel?.description}. Stock at the site is set to exactly this figure.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5">
          {XD.map((s) => (
            <button key={s} onClick={() => setSite(s)} data-testid={`opening-balance-site-${s}`} className={`rounded-sm border px-3 py-1 font-mono text-xs transition-colors ${site === s ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-400 hover:text-white"}`}>{s}</button>
          ))}
        </div>
        <div className="text-xs text-slate-500" data-testid="opening-balance-current">Currently {fmtNum(current)} kg at {site}</div>
        <Input type="number" min={0} value={kg} onChange={(e) => setKg(e.target.value)} placeholder="kg on hand" className={field} data-testid="opening-balance-kg-input" />
        <Button onClick={save} disabled={kg === "" || Number(kg) < 0} className={btn.primary} data-testid="opening-balance-save-btn">Set balance</Button>
      </DialogContent>
    </Dialog>
  );
};

export const PaperStockPanel = ({ reels, fg, onChanged }) => {
  const [edit, setEdit] = useState(null);
  return (
    <Panel title="Paper stock at Group O (kg)" testId="paper-stock-panel" className="mb-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-slate-800"><tr><Th>Reel</Th><Th>Grade</Th><Th>Width</Th><Th>Group O #</Th><Th>Makes mailers</Th>{XD.map((s) => <Th key={s}>{s}</Th>)}<Th>Total</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">
            {reels.map((r) => (
              <tr key={r.part_no} data-testid={`paper-row-${r.part_no}`}>
                <Td className="font-mono text-slate-100">{r.part_no}</Td>
                <Td className="whitespace-nowrap">{r.mondi_grade} <span className="font-mono text-[10px] text-slate-500">{r.mondi_grade_code} · {fmtNum(r.gsm)} gsm</span></Td>
                <Td className="whitespace-nowrap font-mono text-xs">{fmtNum(r.reel_width_in)} in / {fmtNum(r.reel_width_mm)} mm</Td>
                <Td className="font-mono text-xs">{r.groupo_code}</Td>
                <Td className="font-mono text-xs" data-testid={`paper-mailers-${r.part_no}`}>{mailersFor(r, fg).map((m) => <div key={m.part_no} className="whitespace-nowrap">{m.part_no} · {m.description.split(" - ")[0]}</div>)}</Td>
                {XD.map((s) => <Td key={s} className="font-mono" data-testid={`paper-stock-${r.part_no}-${s}`}>{fmtNum(r.sites?.[s]?.on_hand || 0)}</Td>)}
                <Td className="font-mono text-slate-100" data-testid={`paper-total-${r.part_no}`}>{fmtNum(XD.reduce((a, s) => a + (r.sites?.[s]?.on_hand || 0), 0))}</Td>
                <Td><Button size="sm" variant="ghost" onClick={() => setEdit(r)} className={`whitespace-nowrap ${btn.ghost}`} data-testid={`paper-set-balance-${r.part_no}`}><Scale className="mr-1 h-3.5 w-3.5" /> Set opening balance</Button></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BalanceDialog key={edit?.part_no} reel={edit} onClose={() => setEdit(null)} onDone={onChanged} />
    </Panel>
  );
};
